<template>
  <div class="task-description-field-root">
    <!-- Режим редактирования описания -->
    <div
      v-if="isEditing"
      class="task-description-editor rounded-lg border overflow-hidden"
    >
      <EditorContent
        :editor="editor"
        class="task-description-editor-content pa-3"
      />
      <div class="task-description-toolbar border-t px-3 py-2 d-flex align-center justify-space-between">
        <div class="d-flex align-center ga-3 text-medium-emphasis">
          <VTooltip location="top" text="Маркированный список">
            <template #activator="{ props: tipProps }">
              <VIcon
                v-bind="tipProps"
                icon="ri-list-unordered"
                size="18"
                class="cursor-pointer toolbar-icon"
                :class="{ 'text-primary': editor?.isActive('bulletList') }"
                @click="editor?.chain().focus().toggleBulletList().run()"
              />
            </template>
          </VTooltip>
          <VTooltip location="top" text="Нумерованный список">
            <template #activator="{ props: tipProps }">
              <VIcon
                v-bind="tipProps"
                icon="ri-list-ordered"
                size="18"
                class="cursor-pointer toolbar-icon"
                :class="{ 'text-primary': editor?.isActive('orderedList') }"
                @click="editor?.chain().focus().toggleOrderedList().run()"
              />
            </template>
          </VTooltip>
          <VTooltip location="top" text="Полужирный">
            <template #activator="{ props: tipProps }">
              <VIcon
                v-bind="tipProps"
                icon="ri-bold"
                size="18"
                class="cursor-pointer toolbar-icon"
                :class="{ 'text-primary': editor?.isActive('bold') }"
                @click="editor?.chain().focus().toggleBold().run()"
              />
            </template>
          </VTooltip>
          <VTooltip location="top" text="Подчёркнутый">
            <template #activator="{ props: tipProps }">
              <VIcon
                v-bind="tipProps"
                icon="ri-underline"
                size="18"
                class="cursor-pointer toolbar-icon"
                :class="{ 'text-primary': editor?.isActive('underline') }"
                @click="editor?.chain().focus().toggleUnderline().run()"
              />
            </template>
          </VTooltip>
          <VTooltip location="top" text="Заголовок">
            <template #activator="{ props: tipProps }">
              <span
                v-bind="tipProps"
                class="toolbar-text-icon cursor-pointer user-select-none"
                :class="{ 'text-primary': editor?.isActive('heading', { level: 3 }) }"
                @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()"
              >
                Aa
              </span>
            </template>
          </VTooltip>
        </div>
        <VBtn
          variant="text"
          size="small"
          color="primary"
          class="text-none font-weight-medium px-2"
          @click="save"
        >
          Сохранить
          <VIcon icon="ri-check-line" size="18" class="ms-1" />
        </VBtn>
      </div>
    </div>

    <!-- Свернутое состояние: рамка с отформатированным текстом и иконкой карандаша рядом -->
    <VTooltip v-else :disabled="!canEdit" location="top" text="Нажмите для редактирования">
      <template #activator="{ props: tipProps }">
        <div
          v-bind="tipProps"
          class="task-description-box rounded-lg border px-3 py-2 cursor-pointer hover-bg-subtle d-flex align-center"
          :class="{ 'cursor-default': !canEdit }"
          @click="startEdit"
        >
          <div
            v-if="modelValue && !isContentEmpty(modelValue)"
            class="text-body-2 text-high-emphasis task-description-rendered"
            v-html="modelValue"
          />
          <span
            v-else
            class="text-body-2 text-medium-emphasis"
          >
            {{ placeholder }}
          </span>
          <VIcon
            v-if="canEdit"
            icon="ri-edit-line"
            size="16"
            class="text-medium-emphasis ms-2 flex-shrink-0"
          />
        </div>
      </template>
    </VTooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { StarterKit } from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { Placeholder } from '@tiptap/extension-placeholder'

interface Props {
  modelValue?: string | null
  placeholder?: string
  canEdit?: boolean
  initialEditing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: 'Описание',
  canEdit: true,
  initialEditing: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'save', value: string): void
}>()

const isEditing = ref(props.initialEditing)

function isContentEmpty(val?: string | null): boolean {
  if (!val) return true
  const stripped = val.replace(/<[^>]*>/g, '').trim()
  return stripped.length === 0
}

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    StarterKit.configure({
      heading: { levels: [3] },
    }),
    Underline,
    Placeholder.configure({
      placeholder: props.placeholder ?? 'Описание',
    }),
  ],
  onUpdate: () => {
    const html = editor.value?.isEmpty ? '' : (editor.value?.getHTML() || '')
    emit('update:modelValue', html)
  },
})

watch(
  () => props.modelValue,
  newVal => {
    const val = newVal || ''
    const currentHtml = editor.value?.getHTML() || ''
    if (val !== currentHtml && !editor.value?.isFocused) {
      editor.value?.commands.setContent(val, false)
    }
  },
)

onBeforeUnmount(() => {
  editor.value?.destroy()
})

function startEdit() {
  if (!props.canEdit) return
  isEditing.value = true
  nextTick(() => {
    editor.value?.commands.focus('end')
  })
}

function save() {
  const html = editor.value?.isEmpty ? '' : (editor.value?.getHTML() || '')
  isEditing.value = false
  emit('update:modelValue', html)
  emit('save', html)
}
</script>

<style scoped lang="scss">
.task-description-box {
  min-height: 40px;
  background-color: transparent;
  border-color: rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  word-break: break-word;
}

.task-description-editor {
  background-color: rgb(var(--v-theme-surface));
  border-color: rgba(var(--v-border-color), var(--v-border-opacity)) !important;
}

.task-description-editor-content {
  min-height: 120px;
  max-height: 320px;
  overflow-y: auto;
  cursor: text;

  :deep(.ProseMirror) {
    min-height: 100px;
    outline: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    color: rgb(var(--v-theme-on-surface));

    p {
      margin-bottom: 0.25rem;
      &:last-child {
        margin-bottom: 0;
      }
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
      pointer-events: none;
      height: 0;
    }

    h3 {
      font-size: 1.125rem;
      font-weight: 600;
      margin: 0.5rem 0 0.25rem;
      line-height: 1.35;
    }

    ul, ol {
      padding-left: 1.25rem;
      margin: 0.25rem 0;
    }

    u {
      text-decoration: underline;
    }

    strong {
      font-weight: 600;
    }
  }
}

.task-description-rendered {
  word-break: break-word;
  line-height: 20px;

  :deep(p) {
    margin-bottom: 0.25rem;
    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(h3) {
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0.5rem 0 0.25rem;
    line-height: 1.35;
  }

  :deep(ul), :deep(ol) {
    padding-left: 1.25rem;
    margin: 0.25rem 0;
  }

  :deep(u) {
    text-decoration: underline;
  }

  :deep(strong) {
    font-weight: 600;
  }
}

.task-description-toolbar {
  background-color: transparent;
  border-color: rgba(var(--v-border-color), var(--v-border-opacity)) !important;
}

.toolbar-icon:hover,
.toolbar-text-icon:hover {
  color: rgb(var(--v-theme-primary));
}

.toolbar-text-icon {
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
}

.hover-bg-subtle:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}
</style>
